import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PhotoOutlinedIcon from "@mui/icons-material/PhotoOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const shakeBin = keyframes`
  0%, 100% { transform: rotate(0deg) translateY(0); }
  25% { transform: rotate(-8deg) translateY(-2px); }
  50% { transform: rotate(6deg) translateY(0); }
  75% { transform: rotate(-4deg) translateY(-1px); }
`;

const fadeAway = keyframes`
  0%, 20% { opacity: 1; transform: translateY(0) scale(1); }
  55% { opacity: 0.55; transform: translateY(10px) scale(0.92); }
  100% { opacity: 0.15; transform: translateY(28px) scale(0.78); }
`;

const bob = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

function DeletePreviewStage({ variant, accent }) {
  const label =
    variant === "supplier"
      ? "PROVEEDOR"
      : variant === "material"
        ? "MATERIAL"
        : variant === "video"
          ? "VÍDEO"
          : "FOTO";
  const ItemIcon =
    variant === "supplier"
      ? StorefrontOutlinedIcon
      : variant === "material"
        ? StyleOutlinedIcon
        : variant === "video"
          ? VideocamOutlinedIcon
          : PhotoOutlinedIcon;

  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 88,
        mt: 1.35,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: alpha(accent, 0.08),
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        animation: `${rise} 320ms ease both`,
        ...motionSafe,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: "22%",
          top: 14,
          width: 48,
          height: 58,
          borderRadius: 1.25,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.35),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 6px 14px ${alpha(accent, 0.14)}`,
          animation: `${fadeAway} 2.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <ItemIcon sx={{ fontSize: 24, color: accent }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: "18%",
          top: 18,
          width: 52,
          height: 52,
          borderRadius: 1.5,
          bgcolor: alpha(accent, 0.14),
          border: "1.5px solid",
          borderColor: alpha(accent, 0.45),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 8px 18px ${alpha(accent, 0.2)}`,
          animation: `${shakeBin} 2.4s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <DeleteOutlineOutlinedIcon sx={{ fontSize: 28, color: accent }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: 12,
          bottom: 10,
          px: 0.85,
          py: 0.35,
          borderRadius: 1,
          bgcolor: alpha(accent, 0.12),
          border: "1px solid",
          borderColor: alpha(accent, 0.35),
          animation: `${bob} 2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <Typography
          sx={{ fontSize: "0.58rem", fontWeight: 900, color: accent }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Confirmación de borrar foto/vídeo/material de la feria, con motion.
 * @param {{ open: boolean, onClose: () => void, onConfirm: () => void|Promise<void>, deleting?: boolean, isVideo?: boolean, variant?: 'photo'|'video'|'material'|'supplier', itemLabel?: string }} props
 */
export default function FeriaDeletePhotoDialog({
  open,
  onClose,
  onConfirm,
  deleting = false,
  isVideo = false,
  variant: variantProp,
  itemLabel = "",
}) {
  const theme = useTheme();
  const accent = theme.palette.error.main || "#d32f2f";
  const variant = variantProp || (isVideo ? "video" : "photo");
  const title =
    variant === "supplier"
      ? "Eliminar proveedor"
      : variant === "material"
        ? "Eliminar material"
        : variant === "video"
          ? "Eliminar vídeo"
          : "Eliminar foto";
  const bodyNoun =
    variant === "supplier"
      ? "este proveedor"
      : variant === "material"
        ? "este material"
        : variant === "video"
          ? "este vídeo"
          : "esta foto";
  const name = String(itemLabel || "").trim();

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      keepMounted={false}
      transitionDuration={160}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          animation: `${rise} 220ms ease both`,
          ...motionSafe,
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: (th) =>
            `linear-gradient(135deg, ${alpha(th.palette.error.main, 0.12)} 0%, ${alpha(
              th.palette.warning.main,
              0.06,
            )} 100%)`,
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(accent, 0.16),
                color: accent,
                flexShrink: 0,
              }}
            >
              <DeleteOutlineOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, lineHeight: 1.15 }}
              >
                {title}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.4, mt: 0.3 }}
              >
                Esta acción no se puede deshacer
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label="Cancelar"
            onClick={onClose}
            disabled={deleting}
            size="small"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
        <DeletePreviewStage variant={variant} accent={accent} />
      </Box>

      <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            lineHeight: 1.5,
            fontWeight: 600,
            animation: `${rise} 300ms ease both`,
            ...motionSafe,
          }}
        >
          {name
            ? `¿Seguro que quieres eliminar «${name}» de la edición?`
            : `¿Seguro que quieres eliminar ${bodyNoun} de la edición?`}
        </Typography>
      </Box>

      <Stack
        direction="row"
        spacing={1.25}
        justifyContent="flex-end"
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: (th) => alpha(th.palette.background.default, 0.65),
        }}
      >
        <Button
          onClick={onClose}
          disabled={deleting}
          sx={{ fontWeight: 700, minHeight: 44, textTransform: "none" }}
        >
          Cancelar
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={deleting}
          onClick={onConfirm}
          startIcon={
            deleting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DeleteOutlineOutlinedIcon />
            )
          }
          sx={{
            minHeight: 48,
            fontWeight: 800,
            borderRadius: 2,
            textTransform: "none",
            boxShadow: `0 8px 18px ${alpha(accent, 0.28)}`,
          }}
        >
          Eliminar
        </Button>
      </Stack>
    </Dialog>
  );
}
